// Sending, on the Schedule (owner, 2026-11-03: "I don't understand the purpose of
// the sending page … We have schedules already"). There's no Sending page: the
// Schedule says whether mail goes (SendingNotice), and Sending settings… opens
// the few settings there are and how healthy sending is (SendingSettingsDialog).
// Whether mail goes at all is set on the backend (GROWTH_SENDING_ENABLED), never
// here. Only the cap, the re-contact window and the time zone can be changed.

import { api } from '@/api'
import type { SendingSettingsWrite, SendingStatus } from '@/api/growthCampaignTypes'
import { Dialog, EditableTextField, ErrorState, InlineAlert, KeyValueList, LoadingState, RelativeTime, StatTile } from '@/components/ui'
import { formatDateTime } from '@/lib/format'
import { percent } from '@/lib/growthCampaigns'
import { invalidateQueries, setQueryData, useQuery } from '@/lib/query'
import { SafeExternalLink } from './shared'

const useSending = () => useQuery(['growth', 'sending'], (signal) => api.growth.sending.get(signal))

/** Whether mail goes, and what's stopping it, over the Schedule. Nothing when it's on and nothing's wrong. */
export function SendingNotice({ s }: { s: SendingStatus }) {
  return (
    <>
      {s.mode === 'dry_run' ? (
        <InlineAlert tone="info" title="Sending is off">
          Everything below runs, and each email is recorded as a dry run. No email leaves. To turn it on, set GROWTH_SENDING_ENABLED to true on the backend.
        </InlineAlert>
      ) : s.mode === 'test_list' ? (
        <InlineAlert tone="warn" title="Sending is on, for the test list only">
          Real email goes only to the {s.allowlist_count === 1 ? 'one address' : `${s.allowlist_count} addresses`} in GROWTH_TEST_ALLOWLIST. Everyone else is a dry run.
        </InlineAlert>
      ) : null}
      {s.problems.map((p) => (
        <InlineAlert key={p.code} tone="bad" title="Nothing can go until this is fixed">
          {p.message}
        </InlineAlert>
      ))}
    </>
  )
}

function Body({ s }: { s: SendingStatus }) {
  const save = async (patch: SendingSettingsWrite) => {
    const next = await api.growth.sending.update(patch)
    setQueryData(['growth', 'sending'], next)
    invalidateQueries('growth')
  }
  const overLimit = s.rates.bounce_rate > s.rates.bounce_limit
  const whole = (max: number, min: number) => (v: string) => (/^\d+$/.test(v) && Number(v) >= min && Number(v) <= max ? null : `Use a whole number from ${min} to ${max.toLocaleString()}.`)

  return (
    <div className="grid gap-5">
      <section className="grid gap-3">
        <h3 className="text-[13px] font-bold text-ink">Limits</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <EditableTextField label="Daily cap" value={String(s.settings.daily_cap)} maxLength={4} hint="Across every campaign. A campaign has its own too." validate={whole(2000, 1)} onSave={(v) => save({ daily_cap: Number(v) })} />
          <EditableTextField label="Leave alone for (days)" value={String(s.settings.recontact_days)} maxLength={3} hint="An address another campaign just emailed." validate={whole(365, 0)} onSave={(v) => save({ recontact_days: Number(v) })} />
          <EditableTextField className="sm:col-span-2" label="Time zone" value={s.settings.time_zone} mono hint="What “today” means on the Schedule." validate={(v) => (v ? null : 'Enter a time zone, such as America/New_York.')} onSave={(time_zone) => save({ time_zone })} />
        </div>
      </section>

      <section className="grid gap-3">
        <h3 className="text-[13px] font-bold text-ink">How it's going</h3>
        <div className="grid grid-cols-2 gap-3">
          <StatTile label="Today" value={`${s.today.sent + s.today.dry_run} / ${s.today.cap}`} hint={s.today.dry_run ? `${s.today.dry_run} dry run` : 'Sent, of the daily cap'} />
          <StatTile label="Waiting" value={s.today.waiting} hint={s.today.next_send_at ? `Next not before ${formatDateTime(s.today.next_send_at)}` : undefined} />
          <StatTile label={`Bounce rate, ${s.rates.window_days} days`} value={percent(s.rates.bounce_rate)} tone={overLimit ? 'bad' : 'default'} hint={`${s.rates.bounced} of ${s.rates.sent}. A campaign pauses over ${percent(s.rates.bounce_limit)}.`} />
          <StatTile label={`Replies, ${s.rates.window_days} days`} value={s.rates.replied} hint={`${s.rates.unsubscribed} unsubscribed`} />
        </div>
        <p className="text-xs text-muted">
          Spam complaints aren't visible here: a mailbox doesn't report them. Check them in Google Postmaster Tools, and keep the rate under 0.1%. <SafeExternalLink href={s.complaints.tools_url} />
        </p>
      </section>

      <section className="grid gap-3">
        <h3 className="text-[13px] font-bold text-ink">Mailbox</h3>
        <KeyValueList
          items={[
            { label: 'From', value: <span className="break-all">{s.from}</span> },
            { label: 'Sending account', value: s.smtp_configured ? 'Set' : 'Not set' },
            // None for now (owner, November 4); shown once one is set on the server.
            ...(s.mailing_address ? [{ label: 'Postal address', value: s.mailing_address }] : []),
            { label: 'Inbox reader', value: s.imap_configured ? 'Read only' : 'Not set' },
            { label: 'Last read', value: <RelativeTime value={s.inbox.polled_at} /> },
          ]}
        />
        {s.inbox.error ? <InlineAlert tone="bad">{s.inbox.error}</InlineAlert> : null}
        <p className="text-xs text-muted">Replies and bounces are read from the inbox every five minutes. It only looks: nothing there is marked read, moved or labelled.</p>
      </section>
    </div>
  )
}

/** The settings and health that were the Sending page, from the Schedule's header. */
export function SendingSettingsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: s, error, status, refetch } = useSending()
  return (
    <Dialog open={open} onClose={onClose} size="lg" title="Sending settings" description="Campaign email goes from the team mailbox through Google Workspace.">
      {status === 'loading' && !s ? <LoadingState label="Loading…" /> : !s ? <ErrorState error={error} title="Couldn't load sending" onRetry={refetch} /> : <Body s={s} />}
    </Dialog>
  )
}
